import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { hot } from 'react-hot-loader/root';
import { Redirect, useLocation, useParams } from 'react-router-dom';

import Button, { ButtonGroup } from '../../library/button/button';
import { CRUMBS, EVENTS } from '../../library/fitment/fitment-constants';
import Page, { Content, Section } from '../../library/page/page';
import Breadcrumbs from '../../components/breadcrumbs/breadcrumbs';
import CmsHtmlContent from '../../components/cms-html-content/cms-html-content';
import CmsPromotionCarousel from '../../components/cms-promotion-carousel/cms-promotion-carousel';
import CmsSections from '../../components/cms-sections/cms-sections';
import Fitment from '../../components/fitment/fitment';
import FitmentBanner from '../../components/fitment-banner/fitment-banner';
import VehicleLandingPageCallout from './components/vehicle-landing-page-callout/vehicle-landing-page-callout';
import VehicleLandingPageHeroBanner from './components/vehicle-landing-page-hero-banner/vehicle-landing-page-hero-banner';
import VehicleLandingPageShopOther from './components/vehicle-landing-page-shop-other/vehicle-landing-page-shop-other';
import VehicleLandingPageShopTires from './components/vehicle-landing-page-shop-tires/vehicle-landing-page-shop-tires';
import DefaultLayout from '../../layouts/default-layout/default-layout';

import {
  GET_SEO_MAKE,
  GET_SEO_MODEL,
  GET_SEO_TRIM_DESC,
  GET_VEHICLE_DETAILS
} from '../../shared/graphql/common-fitment-queries';
import { GET_VEHICLE_LANDING_PAGE } from './graphql/vehicle-landing-page-queries';

import {
  useDataLayer,
  useFitment,
  useGraphQLClient,
  useNavigateTo,
  usePrevious,
  useProductSearchNavigation,
  useQuery,
  useScrollTo
} from '../../shared/hooks/hooks';

import {
  getTextOverrides,
  interpolateCmsData
} from '../../shared/utils/cms/cms';
import { formatToTitleCase } from '../../shared/utils/format/format';
import { metaTags } from '../../shared/utils/page/page';
import { isArrayWithLength } from '../../shared/utils/validators/validators';
import { getVehicleLandingPageURL } from './utils/vehicle-landing-page-utils';

import {
  PAGE_CATEGORY,
  PAGE_NAME,
  PAGE_SECTION_NAME,
  PAGE_SUBCATEGORY,
  PAGE_TYPE
} from '../../shared/constants/data-layer';
import { FITMENT_MODAL_PATTERN } from '../../shared/constants/modals';
import { SEARCH_TYPES } from '../../shared/constants/search-types';
import {
  CMS_CONTROLLED_TEXT_IDS,
  CMS_SECTION_IDS,
  FITMENT_RESULTS_CATEGORY,
  META_TAG_NAMES
} from './constants/vehicle-landing-page-constants';

import './vehicle-landing-page.scss';

const cmsComponentMap = {
  CmsCalloutComponent: VehicleLandingPageCallout,
  CmsHtmlComponent: CmsHtmlContent,
  CmsPromotionCarouselSectionComponent: CmsPromotionCarousel,
  CmsShoppingCategoriesComponent: VehicleLandingPageShopOther
};

function VehicleLandingPage() {
  const [vehicleDetails, setVehicleDetails] = useState({
    make: null,
    model: null,
    trimDesc: null,
    trimId: null,
    vehicleId: null,
    year: null
  });
  const [fitmentKey, setFitmentKey] = useState(0);
  const [showSelectedFitmentOnMount, setShowSelectedFitmentOnMount] =
    useState(false);
  const [isFitmentSelected, setFitmentSelected] = useState(false);
  const selectedVehicleCrumb =
    process.env.__FF_FITMENT_VEHICLE_CRUMB__ === 'true'
      ? CRUMBS.VEHICLE
      : CRUMBS.MAKE;

  const {
    category: categoryParam,
    make: makeParam,
    model: modelParam,
    trim: trimParam,
    year: yearParam
  } = useParams();
  const category = categoryParam?.toLowerCase?.();
  const client = useGraphQLClient();
  const navigateTo = useNavigateTo();
  const { selectedFitment } = useFitment();
  const previousSelectedFitment = usePrevious(selectedFitment);
  const { hash } = useLocation();
  const { updateDataLayer } = useDataLayer({
    shouldResetPageOnFitmentChange: true
  });
  const { onSearchTypeSelected } = useProductSearchNavigation({
    fitment: selectedFitment
  });
  const scrollTo = useScrollTo('#vlp-product-spotlight', {
    behavior: 'auto',
    offset: 120
  });

  const fitmentPayload = {
    category: categoryParam,
    seoMake: makeParam,
    seoModel: modelParam,
    seoTrim: trimParam,
    seoYear: yearParam
  };

  const {
    data: cmsPageData,
    error: cmsPageError,
    isLoading: isCmsPageLoading
  } = useQuery(GET_VEHICLE_LANDING_PAGE, {
    skip: !makeParam,
    variables: {
      id: getVehicleLandingPageURL(fitmentPayload)
    }
  });
  const { cms } = cmsPageData || {};
  const { page: cmsPage } = cms || {};

  const wasFitmentUnselected = previousSelectedFitment && !selectedFitment;

  const isSelectedVehicle =
    (selectedFitment && !makeParam) ||
    (FITMENT_MODAL_PATTERN.test(hash) &&
      !wasFitmentUnselected &&
      selectedFitment !== previousSelectedFitment);

  const replaceMap = useMemo(
    () => ({
      fitmentMake: isSelectedVehicle
        ? selectedFitment?.make
        : vehicleDetails?.make,
      fitmentModel: isSelectedVehicle
        ? selectedFitment?.model
        : vehicleDetails?.model,
      fitmentTrim: isSelectedVehicle
        ? selectedFitment?.trim
        : vehicleDetails?.trimDesc,
      fitmentYear: isSelectedVehicle
        ? selectedFitment?.year
        : vehicleDetails?.year,
      siteName: window.isAT ? "America's Tire" : 'Discount Tire'
    }),
    [vehicleDetails, selectedFitment, isSelectedVehicle]
  );

  const fitmentData = useMemo(() => {
    if (
      selectedFitment &&
      selectedFitment?.make === vehicleDetails?.make &&
      selectedFitment?.model === vehicleDetails?.model &&
      selectedFitment?.trim === vehicleDetails?.trimDesc &&
      selectedFitment?.year === vehicleDetails?.year
    ) {
      return selectedFitment;
    }

    return {
      make: isSelectedVehicle ? selectedFitment?.make : vehicleDetails?.make,
      model: isSelectedVehicle ? selectedFitment?.model : vehicleDetails?.model,
      trim: isSelectedVehicle
        ? selectedFitment?.trim
        : vehicleDetails?.trimDesc,
      year: isSelectedVehicle ? selectedFitment?.year : vehicleDetails?.year
    };
  }, [vehicleDetails, selectedFitment, isSelectedVehicle]);

  const interpolatedCmsPage = useMemo(
    () => interpolateCmsData(replaceMap, cmsPage),
    [cmsPage, replaceMap]
  );

  const getBaseVLPUrl = category => {
    switch (category) {
      case FITMENT_RESULTS_CATEGORY.FITMENT_ACCESSORIES:
        return `/vehicle-${FITMENT_RESULTS_CATEGORY.FITMENT_ACCESSORIES}`;
      case FITMENT_RESULTS_CATEGORY.FITMENT_PACKAGES:
        return `/vehicle-${FITMENT_RESULTS_CATEGORY.FITMENT_PACKAGES}`;
      case FITMENT_RESULTS_CATEGORY.FITMENT_WHEELS:
        return `/vehicle-${FITMENT_RESULTS_CATEGORY.FITMENT_WHEELS}`;
      default:
        return `/vehicle-${FITMENT_RESULTS_CATEGORY.FITMENT_TIRES}`;
    }
  };

  const crumbs = useMemo(() => {
    const crumbs = [{ name: 'Home', url: '/' }];
    const baseVLPUrl = getBaseVLPUrl(category);

    if (modelParam) {
      crumbs.push({
        name: formatToTitleCase(modelParam),
        url: `${baseVLPUrl}/${makeParam}`
      });
    }

    if (yearParam) {
      crumbs.push({
        name: formatToTitleCase(yearParam),
        url: `${baseVLPUrl}/${makeParam}/${modelParam}`
      });
    }

    return crumbs;
  }, [category, makeParam, modelParam, yearParam]);

  const {
    contentSlots = [],
    documentTitle,
    metaTags: metaData
  } = interpolatedCmsPage || {};
  const [bannerSection, ...cmsSections] = contentSlots;
  const bannerComponent = bannerSection?.cmsComponents[0];

  const metaTagTitle = metaData?.find(
    tag => tag.name === META_TAG_NAMES.META_TITLE
  );
  const textOverrides = getTextOverrides(
    bannerSection?.cmsComponents,
    CMS_CONTROLLED_TEXT_IDS
  );
  const primaryCtaConfig = {
    [FITMENT_RESULTS_CATEGORY.FITMENT_WHEELS]: {
      onClick: () => onSearchTypeSelected(SEARCH_TYPES.WHEELS),
      text: textOverrides?.fitmentShopAllWheelsCta ?? 'View Wheels'
    },
    [FITMENT_RESULTS_CATEGORY.FITMENT_ACCESSORIES]: {
      onClick: () => navigateTo('#/fitment/chooseAccessoryCategory/'),
      text: textOverrides?.fitmentShopAllAccessoriesCta ?? 'View Accessories'
    },
    [FITMENT_RESULTS_CATEGORY.FITMENT_PACKAGES]: {
      onClick: () => navigateTo('#/fitment/tireWheelPackageLink'),
      text: textOverrides?.fitmentShopAllPackagesCta ?? 'View Packages'
    }
  }[category];

  const getVehicleDetails = useCallback(
    ({ forMake = null, forModel = null, forTrimDesc = null, forYear = null }) =>
      client
        .query({
          query: GET_VEHICLE_DETAILS,
          variables: {
            forMake,
            forModel,
            forTrimDesc,
            forYear
          }
        })
        .then(({ data = {} }) => data?.vehicleSelection?.vehicleDetails),
    [client]
  );

  const getSeoMake = useCallback(
    forMake =>
      client
        .query({
          query: GET_SEO_MAKE,
          variables: {
            forMake
          }
        })
        .then(({ data = {} }) => data?.vehicleSelection?.seoMake),
    [client]
  );

  const getSeoModel = useCallback(
    forModel =>
      client
        .query({
          query: GET_SEO_MODEL,
          variables: {
            forModel
          }
        })
        .then(({ data = {} }) => data?.vehicleSelection?.seoModel),
    [client]
  );

  const getSeoTrimDesc = useCallback(
    forTrimId =>
      client
        .query({
          query: GET_SEO_TRIM_DESC,
          variables: {
            forTrimId
          }
        })
        .then(({ data = {} }) => data?.vehicleSelection?.seoTrimDesc),
    [client]
  );

  const onEvent = useCallback(
    ({ name, payload }) => {
      switch (name) {
        case EVENTS.VEHICLE_MAKE_SELECTED: {
          getSeoMake(payload).then(data => {
            if (data) {
              navigateTo(
                getVehicleLandingPageURL({
                  category: categoryParam,
                  seoMake: data
                })
              );
            }
          });
          break;
        }
        case EVENTS.VEHICLE_MODEL_SELECTED: {
          getSeoModel(payload?.description).then(data => {
            if (data) {
              navigateTo(
                getVehicleLandingPageURL({
                  category: categoryParam,
                  seoMake: makeParam,
                  seoModel: data
                })
              );
            }
          });
          break;
        }
        case EVENTS.VEHICLE_YEAR_SELECTED: {
          navigateTo(
            getVehicleLandingPageURL({
              category: categoryParam,
              seoMake: makeParam,
              seoModel: modelParam,
              seoYear: payload?.description
            })
          );
          break;
        }
        case EVENTS.VEHICLE_TRIM_SELECTED: {
          getSeoTrimDesc(payload?.id).then(data => {
            if (data) {
              navigateTo(
                getVehicleLandingPageURL({
                  category: categoryParam,
                  seoMake: makeParam,
                  seoModel: modelParam,
                  seoTrim: data,
                  seoYear: yearParam
                })
              );
            }
          });
          break;
        }
        case EVENTS.VEHICLE_FITMENT_CRUMBS_SHOWING: {
          setFitmentSelected(!payload);
          break;
        }
        case EVENTS.VEHICLE_FITMENT_UPDATED: {
          setFitmentSelected(true);
          break;
        }
        case EVENTS.VEHICLE_FITMENT_SELECTED: {
          setFitmentSelected(true);
          break;
        }
        case EVENTS.VEHICLE_RECENT_FITMENT_REMOVED: {
          setFitmentSelected(false);
          break;
        }
        case EVENTS.VEHICLE_SAVED_FITMENT_REMOVED: {
          setFitmentSelected(false);
          break;
        }
        case EVENTS.VEHICLE_FITMENT_UPDATE_PENDING: {
          setShowSelectedFitmentOnMount(true);
          break;
        }
      }
    },
    [
      categoryParam,
      makeParam,
      modelParam,
      yearParam,
      navigateTo,
      getSeoMake,
      getSeoModel,
      getSeoTrimDesc
    ]
  );

  function shouldRenderCmsSection(section) {
    switch (section?.name) {
      case CMS_SECTION_IDS.ATCC:
        return window.isAT;
      case CMS_SECTION_IDS.CALLOUT_MAKE:
        return true;
      case CMS_SECTION_IDS.CALLOUT_MODEL:
        return true;
      case CMS_SECTION_IDS.CALLOUT_YEAR:
        return true;
      case CMS_SECTION_IDS.DEALS:
        return true;
      case CMS_SECTION_IDS.DTCC:
        return window.isDT;
      case CMS_SECTION_IDS.SHOP_BY_OTHER:
        return isFitmentSelected;
      default:
        return false;
    }
  }

  function shouldHideCmsSectionHeading(section) {
    return (
      ['Section1', 'Section1B', 'Section1E'].indexOf(section?.position) > -1
    );
  }

  useEffect(() => {
    updateDataLayer({
      [PAGE_CATEGORY]: 'vehicle_landing',
      [PAGE_NAME]: 'vehicle_landing',
      [PAGE_SECTION_NAME]: metaTagTitle?.content,
      [PAGE_SUBCATEGORY]: `vehicle_landing_${makeParam}`,
      [PAGE_TYPE]: 'vehicle_landing'
    });
  }, [metaTagTitle, makeParam, updateDataLayer]);

  useEffect(() => {
    async function vehicleDetailsFromURL() {
      try {
        const vehicleDetailsResponse = await getVehicleDetails({
          forMake: makeParam,
          forModel: modelParam || null,
          forTrimDesc: trimParam || null,
          forYear: yearParam || null
        });

        if (vehicleDetailsResponse) {
          setVehicleDetails({
            make: vehicleDetailsResponse?.make,
            model: vehicleDetailsResponse?.model,
            trimDesc: vehicleDetailsResponse?.trimDesc,
            trimId: vehicleDetailsResponse?.trimId,
            vehicleId: vehicleDetailsResponse?.vehicleId,
            year: vehicleDetailsResponse?.year
          });
        } else {
          throw new Error('No vehicle found.');
        }
      } catch {
        navigateTo('/', true);
      }
    }

    if (makeParam) {
      vehicleDetailsFromURL();
    }
  }, [
    makeParam,
    modelParam,
    yearParam,
    trimParam,
    navigateTo,
    getVehicleDetails
  ]);

  useEffect(() => {
    async function selectFitmentDetails() {
      try {
        const [seoMake, seoModel, seoTrim] = await Promise.all([
          getSeoMake(selectedFitment?.make),
          getSeoModel(selectedFitment?.model),
          getSeoTrimDesc(selectedFitment?.trimId)
        ]);
        navigateTo(
          getVehicleLandingPageURL({
            category: categoryParam,
            seoMake,
            seoModel,
            seoTrim,
            seoYear: selectedFitment?.year
          }) + (hash || '')
        );
      } catch {
        navigateTo('/404');
      }
    }
    if (isSelectedVehicle) {
      setFitmentKey(fk => fk + 1);
      setShowSelectedFitmentOnMount(true);
      selectFitmentDetails();
    }
  }, [
    hash,
    selectedFitment,
    categoryParam,
    isSelectedVehicle,
    getSeoMake,
    getSeoModel,
    getSeoTrimDesc,
    navigateTo
  ]);

  useEffect(() => {
    if (hash.includes('vlp-product-spotlight')) {
      scrollTo();
    }
  });

  useEffect(() => {
    if (wasFitmentUnselected) {
      navigateTo('/');
    }
  }, [wasFitmentUnselected, navigateTo]);

  if (
    (cmsPageError && !window.isPreRendering) ||
    (makeParam && !isCmsPageLoading && !cmsPage)
  ) {
    return <Redirect to="/404" />;
  }

  return (
    <DefaultLayout>
      <Page>
        {!window.isPreRendering && (
          <Helmet>
            {documentTitle && <title>{documentTitle}</title>}
            {metaTags(metaData)}
            <meta
              content={makeParam ? 'index, follow' : 'noindex, nofollow'}
              name="robots"
            />
          </Helmet>
        )}
        <Breadcrumbs crumbs={crumbs} styleName="crumbs" />
        <div styleName="vlp-hero-container">
          {isArrayWithLength(bannerComponent?.slides) && (
            <VehicleLandingPageHeroBanner bannerComponent={bannerComponent} />
          )}
        </div>

        <Content isFullWidth>
          <Section styleName="fitment-section">
            <div styleName="fitment-container">
              <Fitment
                hideBackButton
                hideLicensePlate
                hideTabs
                isShowingRecentFitments={false}
                isYearAfterModel
                key={fitmentKey}
                onEvent={onEvent}
                renderSelectedFitment={({
                  isEditing,
                  maxSize,
                  onEditFitment
                }) => (
                  <>
                    <FitmentBanner
                      fitment={selectedFitment}
                      maxSize={maxSize}
                      onEditFitment={() => {
                        setShowSelectedFitmentOnMount(false);
                        onEditFitment();
                      }}
                      showEditLink={!isEditing}
                      styleName="fitment-banner"
                    />
                    {category === FITMENT_RESULTS_CATEGORY.FITMENT_TIRES && (
                      <VehicleLandingPageShopTires
                        selectedFitment={selectedFitment}
                        textOverrides={textOverrides}
                      />
                    )}

                    {primaryCtaConfig && (
                      <ButtonGroup styleName="cta-container">
                        <Button isPrimary onClick={primaryCtaConfig.onClick}>
                          {primaryCtaConfig.text}
                        </Button>
                      </ButtonGroup>
                    )}
                  </>
                )}
                selectedVehicleCrumb={
                  makeParam && !showSelectedFitmentOnMount
                    ? selectedVehicleCrumb
                    : ''
                }
                selectedVehicleDetails={
                  showSelectedFitmentOnMount ? undefined : vehicleDetails
                }
                textOverrides={textOverrides}
              />
            </div>
          </Section>
          {cmsSections.length > 0 && (
            <CmsSections
              category={category}
              componentMap={cmsComponentMap}
              fitment={fitmentData}
              sections={cmsSections.filter(shouldRenderCmsSection)}
              shouldHideHeading={shouldHideCmsSectionHeading}
              shouldHideSectionHeading={shouldHideCmsSectionHeading}
              styleName="cms-sections"
            />
          )}
        </Content>
      </Page>
    </DefaultLayout>
  );
}

export default hot(VehicleLandingPage);
